import React, { useEffect, useState } from 'react';
import { Box, Card, CircularProgress, Grid } from '@mui/material';
import { Formalite, useFormaliteService } from '@europrocurement/l2d-domain';
import {
    DatatablePagination,
    FlexyDatatable,
    FlexyDatatableProps,
} from '@europrocurement/flexy-datatable';
import { useNavigate } from 'react-router';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import NoResultPreview from '../../components/NoResultPreview';
import TableRowActions from '../../components/TableRowActions';

type FormalityCompatibilitiesCardProps = {
    formalityId: number;
    totalItemsCount: number;
    onRemoveCompatibility: (compatibilityId: string | number) => void;
};

const FormalityCompatibilitiesCard: React.FunctionComponent<FormalityCompatibilitiesCardProps> =
    function (props) {
        const { formalityId, onRemoveCompatibility } = props;
        const navigate = useNavigate();
        const { formalityModel: model } = useFormaliteService();
        const { request, requestState } = useApiRequest();
        const [compatibilities, setCompatibilities] = useState([]);
        const [page, setCurrentPage] = useState(0);
        const [itemsPerPage, setItemsPerPage] = useState(10);
        const [totalItemsCount, setTotalItemsCount] = useState(0);
        const pagination: DatatablePagination = {
            page,
            itemsPerPage,
            total: totalItemsCount,
            onPageChange: (newValue) => {
                setCurrentPage(newValue);
            },
            onItemsPerPageChange: (newValue) => {
                setCurrentPage(0);
                setItemsPerPage(newValue);
            },
        };

        const refreshList = () => {
            const query = {
                page: page + 1,
                itemsPerPage,
                compatibleWith: formalityId,
                deleted: false,
            };
            request(model.list(query), {
                successCallback: (response) => {
                    setCompatibilities(response.data['hydra:member']);
                    setTotalItemsCount(response.data['hydra:totalItems'] as number);
                },
                withToaster: false,
            });
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(refreshList, [formalityId, page, itemsPerPage]);

        if (requestState.isProcessing) {
            return (
                <Grid
                    container
                    justifyContent="center"
                    sx={{ marginTop: '25px' }}
                >
                    <CircularProgress />
                </Grid>
            );
        }

        if (!compatibilities.length) {
            return (
                <Card>
                    <NoResultPreview message="Cette formalité ne comporte aucune compatibilité" />
                </Card>
            );
        }

        const celStyle = {
            display: 'flex',
            alignItems: 'center',
            minHeight: '40px',
        };
        const columns = [
            {
                label: 'Nom',
                field: 'libelle',
                isDisplayed: true,
                render: (item: Formalite) => <Box style={celStyle}>{item.libelle}</Box>,
            },
            {
                label: 'Catégorie',
                field: 'categorie',
                isDisplayed: true,
                render: (item: Formalite) => <Box style={celStyle}>{item?.categorie?.libelle}</Box>,
            },
            {
                label: 'Actions',
                displayLabel: false,
                isDisplayed: true,
                render: (item: Formalite) => (
                    <TableRowActions
                        itemId={item.id as unknown as string}
                        removeItem={() => {
                            onRemoveCompatibility(item?.id as unknown as string);
                        }}
                    />
                ),
            },
        ];

        const datatableProps: FlexyDatatableProps = {
            status: 'succeeded',
            data: compatibilities,
            hideColumnOptions: true,
            pagination,
            columns,
            onClickRow: (_: React.MouseEvent<HTMLElement>, { id }: Formalite) =>
                navigate(`/formalites/liste/${id}`),
        };

        return (
            <Card>
                <FlexyDatatable {...datatableProps} />
            </Card>
        );
    };

export default FormalityCompatibilitiesCard;
